import { motion } from "framer-motion";
import type { ReactNode } from "react";

/**
 * Scroll reveal. Short, once, and small in distance — the brief asks for
 * motion that is felt rather than watched, and long travel on a marketing
 * page reads as a page that has not finished loading.
 *
 * The global `prefers-reduced-motion` rule in styles.css flattens CSS
 * animation and transition durations, but it cannot reach framer-motion's
 * JS-driven transforms, so the guard is repeated here via `useReducedMotion`
 * at the call sites that animate continuously. For one-shot reveals the
 * element still ends at opacity 1 either way, so the reduced-motion
 * outcome is simply a faster arrival, never missing content.
 */
export function Reveal({
  children,
  delay = 0,
  y = 16,
  className,
  as = "div",
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  as?: "div" | "li" | "section" | "article";
}) {
  const MotionTag = motion[as];
  return (
    <MotionTag
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-64px" }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </MotionTag>
  );
}
